import {
  Box, Button, styled, TextField, Typography, useTheme,
} from '@mui/material';
import { red } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import CallToActionButton from '../../../common/CallToActionButton';
import { PaymentMethodStatus, PayoutDialogContentType } from '../../../common/enums';
import { useAppSelector } from '../../../store/hooks';
import { selectBankAccount, selectPaperCheque, selectPayPalAccount } from '../../../store/slices/dashboardSlice';
import { PaymentMethodType } from '../../../common/models/PaymentMethod';
import { TestIds } from '../../../assets/testIds';

export const TransparentPayoutCTA = ({ text, onClick, disable = false }: {
  text: string,
  onClick?: () => void,
  disable?: boolean,
}) => {
  const theme = useTheme();
  return (
    <CallToActionButton
      data-testid={TestIds.managePayoutSettings.confirmDeleteBtn}
      text={text}
      onClick={onClick}
      disable={disable}
      sx={{
        position: 'static',
        width: '100% !important',
        height: 'revert',
        padding: `${theme.spacing(1.25)} ${theme.spacing(1.75)}`,
        color: red[500],
        background: 'transparent',
        borderRadius: theme.spacing(12.5),
        border: `${theme.spacing(0.125)} solid ${red[500]}`,
        fontSize: `${theme.spacing(2)} !important`,
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: 'transparent !important',
          background: 'transparent !important',
          boxShadow: 'none',
        },
        '&:disabled': {
          backgroundColor: 'transparent',
          opacity: '0.4',
          color: red[500],
        },
        [theme.breakpoints.up('sm')]: {
          marginLeft: theme.spacing(0),
        },
      }}
    />
  );
};

export const PayoutCTA = ({ text, onClick, disable = false }: {
  text: string,
  onClick?: () => void;
  disable?: boolean
}) => {
  const theme = useTheme();
  return (
    <CallToActionButton
      text={text}
      type={'submit'}
      disable={disable}
      onClick={onClick}
      sx={{
        position: 'static',
        width: '100% !important',
        height: 'revert',
        padding: `${theme.spacing(1.25)} ${theme.spacing(1.75)}`,
        background: theme.palette.brand.primary,
        borderRadius: theme.spacing(12.5),
        fontSize: `${theme.spacing(2)} !important`,
        '&:hover': {
          backgroundColor: theme.palette.brand.primary,
        },
        '&:disabled': {
          backgroundColor: theme.palette.brand.primary,
          opacity: '0.4',
          color: theme.palette.common.white,
        },
        [theme.breakpoints.up('sm')]: {
          marginLeft: theme.spacing(0),
        },
      }}
    />
  );
};

export const GoBackLink = ({
  setDialogContentType,
  setDialogSubTitle,
}: {
  setDialogContentType?: (event: PayoutDialogContentType) => void,
  setDialogSubTitle?: (event: string | JSX.Element) => void,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (<Button
    data-testid={TestIds.managePayoutSettings.goBackBtn}
    onClick={() => {
      setDialogContentType?.(PayoutDialogContentType.PAYMENT_METHOD_LIST);
      setDialogSubTitle?.(`${t('wallet.payoutSettingsDialog.subTitle.manageAccount')}`);
    }}
    sx={{
      textDecoration: 'underline',
      display: 'flex',
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
      justifyContent: 'center',
      background: theme.palette.common.white,
      color: theme.palette.common.black,
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      },
    }}
  >
    <Typography
      variant='h6'>
      {t('homeDepot.instructions.completionNo')}
    </Typography>
  </Button>
  );
};

export const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(1.5),
  '& fieldset': {
    borderRadius: theme.spacing(1.5),
    border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[200],
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[200],
  },
})) as unknown as typeof TextField;

export const RequiredMark = () => {
  const theme = useTheme();
  return (
    <span style={{
      color: theme.palette.brand.primary,
      marginLeft: theme.spacing(0.5),
    }}>*</span>
  );
};

export const FieldBox = ({ children, name, isRequired = true }: {
  children: JSX.Element, name: string, isRequired?: boolean,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        alignSelf: 'stretch',
        alignItems: 'flex-start',
        flex: '1 0 0',
      }}
    >
      <Typography variant='h6'>
        {name}
        {isRequired && <RequiredMark />}
      </Typography>
      {children}
    </Box>
  );
};

export const getBankAccountNumber = (
  input: string | undefined,
): string => {
  if (!input) return ''; // Handle empty or undefined cases
  return input.slice(-4);
};

export const PaymentMethodDetails = () => {
  const payPalAccount = useAppSelector(selectPayPalAccount);
  const bankAccount = useAppSelector(selectBankAccount);
  const paperCheque = useAppSelector(selectPaperCheque);
  return {
    paypal: {
      id: payPalAccount?.id,
      type: PaymentMethodType.PAYPAL,
      name: 'PayPal',
      account: payPalAccount?.meta?.paypalAccountId,
      paymentMethodExist:
        !!(payPalAccount && payPalAccount?.status !== PaymentMethodStatus.UNLINKED),
      isActive: payPalAccount?.active,
      status: payPalAccount?.status,
    },
    bank_account: {
      id: bankAccount?.id,
      type: PaymentMethodType.BANK_ACCOUNT,
      name: 'Bank Transfer',
      account: `**** ${getBankAccountNumber(bankAccount?.meta?.accountNumber)}`,
      paymentMethodExist:
        !!(bankAccount && bankAccount?.status !== PaymentMethodStatus.UNLINKED),
      isActive: bankAccount?.active,
      status: bankAccount?.status,
    },
    paper_cheque: {
      id: paperCheque?.id,
      type: PaymentMethodType.PAPER_CHEQUE,
      name: 'Paper Check',
      account: paperCheque?.meta?.businessName || paperCheque?.meta?.addressLine1 || 'Paper Check',
      paymentMethodExist:
        !!(paperCheque && paperCheque?.status !== PaymentMethodStatus.UNLINKED),
      isActive: paperCheque?.active,
      status: paperCheque?.status,
    },
  };
};
